import { ExtrudeCutDefinitionSnapshotBuilder } from '../../runtime/ExtrudeCutDefinitionSnapshotBuilder';
import { ExtrudeFeatureEditDefinitionSanitizer } from './ExtrudeFeatureEditDefinitionSanitizer';
import type { ExtrudeFeatureEditDefinition } from './ExtrudeFeatureEditDefinition';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/extrude 层；
 * 2. 负责把编辑定义映射为可执行的拉伸命令参数；
 * 3. 该类只处理命令参数组装，不承担默认值生成或特征定义对象创建职责。
 *
 * 文件职责：
 * 1. 从编辑定义生成 legacy depth / direction / startOffset；
 * 2. 输出 worker / feature definition 可直接消费的命令参数快照；
 * 3. 统一收口 cutOptions 的纯数据写回。
 */
export class ExtrudeFeatureCommandParamsBuilder {
    /**
     * 构建兼容旧执行链仍需使用的扁平载荷。
     *
     * @param definition 已净化的编辑定义。
     * @returns legacy 扁平载荷。
     */
    public static buildLegacyPayload(definition: ExtrudeFeatureEditDefinition): {
        depth: number;
        direction: 'normal' | 'reverse';
        startOffset: number;
    } {
        if (definition.direction1.endCondition === 'mid_plane') {
            return { depth: definition.direction1.depth, direction: 'normal', startOffset: -definition.direction1.depth / 2 };
        }
        if (definition.direction2.enabled) {
            return {
                depth: definition.direction1.depth + definition.direction2.depth,
                direction: definition.direction1.reverse ? 'reverse' : 'normal',
                startOffset: definition.direction1.reverse
                    ? definition.direction2.depth
                    : -definition.direction2.depth,
            };
        }

        return {
            depth: definition.direction1.depth,
            direction: definition.direction1.reverse ? 'reverse' : 'normal',
            startOffset: 0,
        };
    }

    /**
     * 根据编辑定义构建可执行拉伸命令参数。
     *
     * @param baseParams 基础参数。
     * @param definition 编辑定义。
     * @returns 完整拉伸命令参数。
     */
    public static buildCommandParams(
        baseParams: Omit<
            ExtrudeParams,
            'depth' | 'direction' | 'startOffset' | 'mergeResult' | 'direction1' | 'direction2' | 'thinFeature'
        >,
        definition: ExtrudeFeatureEditDefinition,
    ): ExtrudeParams {
        const sanitizedDefinition = ExtrudeFeatureEditDefinitionSanitizer.sanitize(definition);
        return this.buildCommandParamsFromSanitized(baseParams, sanitizedDefinition);
    }

    /**
     * 根据已净化定义构建命令参数。
     *
     * @param baseParams 基础参数。
     * @param definition 已净化编辑定义。
     * @returns 完整拉伸命令参数。
     */
    public static buildCommandParamsFromSanitized(
        baseParams: Omit<
            ExtrudeParams,
            'depth' | 'direction' | 'startOffset' | 'mergeResult' | 'direction1' | 'direction2' | 'thinFeature'
        >,
        definition: ExtrudeFeatureEditDefinition,
    ): ExtrudeParams {
        const legacyPayload = this.buildLegacyPayload(definition);
        const referenceBindings = (baseParams.referenceBindings ?? []).filter((binding) => (
            binding.kind !== 'extrude-reference' && (binding.kind !== 'model-topology' || !binding.role?.startsWith('extrude-'))
        ));
        for (const directionKey of ['direction1', 'direction2'] as const) {
            if (directionKey === 'direction2' && definition.direction1.endCondition === 'mid_plane') continue;
            const direction = definition[directionKey];
            const target = direction.target;
            if (!direction.enabled || !target || ['blind', 'mid_plane', 'through_all'].includes(direction.endCondition)) continue;
            if (target.kind === 'sketch-point' || target.kind === 'datum-plane') {
                referenceBindings.push({ kind: 'extrude-reference', role: directionKey === 'direction1' ? 'extrude-direction1' : 'extrude-direction2', target });
                continue;
            }
            referenceBindings.push({
                kind: 'model-topology',
                role: directionKey === 'direction1' ? 'extrude-direction1' : 'extrude-direction2',
                bodyObjectId: target.kind === 'body' ? target.bodyObjectId : target.selection.bodyObjectId,
                items: target.kind === 'model-topology' ? [target.selection] : undefined,
            });
        }

        return {
            ...baseParams,
            referenceBindings: referenceBindings.length ? referenceBindings : undefined,
            featureKind: definition.featureKind,
            operation: definition.operation,
            depth: legacyPayload.depth,
            direction: legacyPayload.direction,
            startOffset: legacyPayload.startOffset,
            mergeResult: definition.mergeResult,
            direction1: definition.direction1,
            direction2: definition.direction2,
            thinFeature: definition.thinFeature,
            cutOptions: ExtrudeCutDefinitionSnapshotBuilder.clone(definition.cutOptions) ?? undefined,
        };
    }
}
